// src/components/CustomNodes.jsx
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useReactFlow, Handle, Position } from 'reactflow';
import { Chip, IconButton, CircularProgress } from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import CloseIcon from '@mui/icons-material/Close';
import { findPathToStart } from '../utils/pathHelper';
import AudioPlayer from './AudioPlayer';
import { Box } from '@mui/material';

// Dialogue Node
export const DialogueNode = ({ data, selected, id }) => {
  const { setNodes, setEdges, getNodes, getEdges } = useReactFlow();
  const [isEditable, setIsEditable] = useState(false);
  const textareaRef = useRef(null);
  const clickCountRef = useRef(0);
  const selectedClickRef = useRef(0);
  const answerRefs = useRef([]);
  const lastTextRef = useRef(''); // Для хранения последнего выведенного текста
  const typingTimerRef = useRef(null); // Для таймера
  const lastTypingRef = useRef(Date.now()); // Для отслеживания времени последнего ввода
  const revisionRef = useRef(0); // Добавляем счетчик ревизий
  const [suggestion, setSuggestion] = useState(''); // Для хранения подсказки
  const [isTranslating, setIsTranslating] = useState(false);
  const activeLanguage = data.activeLanguage || 'ru';
  const API_BASE_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:4000' : '';

  const getCurrentText = useCallback(() => {
    return activeLanguage === 'en' ? (data.enText || '') : (data.text || '');
  }, [activeLanguage, data.enText, data.text]);

  const getCurrentAnswer = useCallback((answer) => {
    return activeLanguage === 'en' ? (answer.enText || '') : (answer.text || '');
  }, [activeLanguage]);
  
  useEffect(() => {
    if (selected) {
      selectedClickRef.current = clickCountRef.current;
    }
  }, [selected]);

  const updateTextAreaHeight = (textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    updateTextAreaHeight(textareaRef.current);
    data.answers?.forEach((_, index) => {
      updateTextAreaHeight(answerRefs.current[index]);
    });
  }, [data.text, data.answers]);

  useEffect(() => {
    if (!selected) {
      setIsEditable(false);
      setNodes((nds) =>
        nds.map((node) => ({
          ...node,
          draggable: true,
        }))
      );
    }
  }, [selected, setNodes]);

  useEffect(() => {
    if (isEditable && textareaRef.current) {
      const length = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(length, length);
    }
  }, [isEditable]);

  // Функция для проверки и вывода истории
  const checkAndPrintHistory = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const currentText = textarea.value;
    
    // Проверяем, что текст изменился с последнего вывода
    if (currentText === lastTextRef.current) return;
    
    // Проверяем позицию курсора и последнюю строку
    if (textarea.selectionStart === textarea.value.length && 
        textarea.selectionEnd === textarea.value.length) {
      
      const lines = textarea.value.split('\n');
      const lastLine = lines[lines.length - 1];
      
      // Не выводим если последняя строка содержит Emoji или заканчивается на ?.!
      if (!lastLine.includes('Emoji') && !/[?.!]$/.test(lastLine)) {
        const result = findPathToStart(getNodes(), getEdges(), id);
        if (result?.messages) {
          //console.log('Conversation history:');
          //result.messages.forEach(msg => console.log(msg));
          lastTextRef.current = currentText; // Запоминаем текст
          
          // Запоминаем текущую ревизию
          const revisionAtRequest = revisionRef.current;

          console.log(`flowId: ${data.flowId}`);

          // Отправляем запрос
          fetch('/api/txtmodel/helper', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              prompt: result.messages.join('\n'),
              flowId: data.flowId || ''
            })
          })
          .then(response => response.json())
          .then(data => {
            // Принимаем ответ только если:
            // 1. Текст и ревизия не изменились
            // 2. Ответ не содержит эмодзи
            if (lastTextRef.current === textarea.value && 
                revisionAtRequest === revisionRef.current &&
                data.answer &&
                !/[\u{1F300}-\u{1F9FF}]/u.test(data.answer)) {
              
              let processedAnswer = data.answer;
              
              // Если подсказка начинается с пробела и текст заканчивается пробелом,
              // удаляем начальный пробел из подсказки
              if (processedAnswer.startsWith(' ') && textarea.value.endsWith(' ')) {
                processedAnswer = processedAnswer.substring(1);
              }
              
              // Если подсказка не заканчивается на ?.! добавляем точку
              if (!/[?.!]$/.test(processedAnswer)) {
                processedAnswer += '.';
              }
              
              console.log('AI Helper:', processedAnswer);
              setSuggestion(processedAnswer);
            }
          })
          .catch(error => {
            console.error('Error:', error);
          });
        }
      }
    }
  }, [getNodes, getEdges, id, data.flowId]);

  // Обработчик изменения текста
  const handleTextChange = useCallback((e) => {
    const newValue = e.target.value;
    
    // Увеличиваем счетчик ревизий и обнуляем подсказку при изменении текста
    revisionRef.current++;
    setSuggestion('');
    
    // Обновляем время последнего ввода
    lastTypingRef.current = Date.now();
    
    // Сбрасываем предыдущий таймер
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }
    
    // Устанавливаем новый таймер
    typingTimerRef.current = setTimeout(() => {
      // Проверяем, прошла ли секунда с последнего ввода
      if (Date.now() - lastTypingRef.current >= 1000) {
        checkAndPrintHistory();
      }
    }, 1000);

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          const targetField = activeLanguage === 'en' ? 'enText' : 'text';
          return {
            ...node,
            data: {
              ...node.data,
              [targetField]: newValue
            }
          };
        }
        return node;
      })
    );

    updateTextAreaHeight(e.target);
  }, [setNodes, id, checkAndPrintHistory, activeLanguage]);

  // Обработчик клика
  const handleTextClick = useCallback((e) => {
    e.stopPropagation();
    clickCountRef.current++;
    
    if (selected && clickCountRef.current > selectedClickRef.current + 1) {
      setIsEditable(true);
      setNodes((nds) =>
        nds.map((node) => ({
          ...node,
          draggable: node.id !== id,
        }))
      );
      
      checkAndPrintHistory();
    }
  }, [selected, id, setNodes, checkAndPrintHistory]);

  // Очистка таймера при размонтировании
  useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
    };
  }, []);

  const onBlur = useCallback(() => {
    setIsEditable(false);
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        draggable: true,
      }))
    );
  }, [setNodes]);

  // Добавляем обработчик для добавления ответов
  const handleAddAnswer = useCallback((e) => {
    e.stopPropagation();
    
    // Удаляем существующие соединения с дефолтным выходом
    setEdges((eds) => 
      eds.filter(edge => !(
        edge.source === id && 
        edge.sourceHandle === 'default'
      ))
    );
    
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              answers: [...(node.data.answers || []), { text: '', enText: '' }]
            }
          };
        }
        return node;
      })
    );
  }, [id, setNodes, setEdges]);

  // Обработчик изменения текста ответа
  const handleAnswerChange = useCallback((index, newValue) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          const targetField = activeLanguage === 'en' ? 'enText' : 'text';
          const newAnswers = [...(node.data.answers || [])];
          newAnswers[index] = { ...newAnswers[index], [targetField]: newValue };
          return {
            ...node,
            data: {
              ...node.data,
              answers: newAnswers
            }
          };
        }
        return node;
      })
    );
    updateTextAreaHeight(answerRefs.current[index]);
  }, [id, setNodes, activeLanguage]);

  // Обработчик клика по ответу
  const handleAnswerClick = useCallback((e) => {
    e.stopPropagation();
    clickCountRef.current++;
    
    if (selected && clickCountRef.current > selectedClickRef.current + 1) {
      setIsEditable(true);
      setNodes((nds) =>
        nds.map((node) => ({
          ...node,
          draggable: node.id !== id,
        }))
      );
    }
  }, [selected, id, setNodes]);

  const handleDeleteAnswer = useCallback((index, e) => {
    e.stopPropagation();
    
    // Удаляем ответ из ноды
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          const newAnswers = [...(node.data.answers || [])];
          newAnswers.splice(index, 1);
          return {
            ...node,
            data: {
              ...node.data,
              answers: newAnswers
            }
          };
        }
        return node;
      })
    );

    // Удаляем соответствующее соединение
    setEdges((eds) => 
      eds.filter(edge => !(
        edge.source === id && 
        edge.sourceHandle === `answer-${index}`
      ))
    );
  }, [id, setNodes, setEdges]);

  const hasAnswers = data.answers && data.answers.length > 0;

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        selected: node.id === id
      }))
    );
  }, [id, setNodes]);

  // Добавляем обработчик клавиатуры
  const handleKeyDown = useCallback((e) => {
    if (isEditable) return; // Не удаляем во время редактирования
    
    if (selected && (e.key === 'Delete' || e.key === 'Backspace')) {
      e.stopPropagation();
      setNodes((nds) => nds.filter(node => node.id !== id));
    }

    if (e.key === 'Tab' && suggestion) {
      e.preventDefault(); // Предотвращаем стандартное поведение Tab
      
      // Обновляем текст ноды, добавляя подсказку
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                text: node.data.text + suggestion
              }
            };
          }
          return node;
        })
      );
      
      // Обнуляем подсказку
      setSuggestion('');
    }
  }, [selected, isEditable, id, setNodes, suggestion]);

  const handleDeleteAudio = useCallback(() => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          const { audioUrl, audioName, ...restData } = node.data;
          return {
            ...node,
            data: restData
          };
        }
        return node;
      })
    );
  }, [id, setNodes]);

  // RU -> EN translation with history and answers
  const handleTranslate = useCallback(() => {
    if (isTranslating) return;
    const ruText = data.text || '';
    const ruAnswers = (data.answers || []).map(a => a.text || '').filter(Boolean);
    if (!ruText.trim() && ruAnswers.length === 0) return;

    setIsTranslating(true);

    const result = findPathToStart(getNodes(), getEdges(), id);
    let history = result?.messages ? result.messages.join('\n') : '';
    const MAX_HISTORY_CHARS = 1000;
    if (history && history.length > MAX_HISTORY_CHARS) {
      history = history.slice(-MAX_HISTORY_CHARS);
    }

    let textToTranslate = ruText || '';
    if (ruAnswers.length > 0) {
      if (textToTranslate.trim()) {
        textToTranslate += `\n\nAnswers:\n${ruAnswers.join('\n')}`;
      } else {
        textToTranslate = `Translate only the following answers:\n${ruAnswers.join('\n')}`;
      }
    }

    fetch(`${API_BASE_URL}/api/grok/translate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        text: textToTranslate,
        history,
        flowId: data.flowId || '',
        fromLang: 'ru',
        toLang: 'en'
      })
    })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`HTTP ${res.status}: ${text.slice(0, 120)}`);
        }
        return res.json();
      })
      .then(payload => {
        if (!payload || !payload.translatedText) return;
        const clean = payload.translatedText.replace(/```json\n|\n```/g, '').trim();
        // Robustly parse Answers: section (allow extra spaces and list markers)
        const lower = clean.toLowerCase();
        const marker = 'answers:';
        let mainText = clean;
        let answersText = '';
        const idx = lower.indexOf(marker);
        if (idx !== -1) {
          mainText = clean.slice(0, idx).trim();
          answersText = clean.slice(idx + marker.length).trim();
          // remove optional colon/newlines/spaces at start
          answersText = answersText.replace(/^\s*/,'');
        }

        setNodes((nds) => nds.map((node) => {
          if (node.id !== id) return node;
          const updated = { ...node };
          updated.data = { ...updated.data };
          if ((data.text || '').trim() && mainText.trim()) {
            updated.data.enText = mainText.trim();
          }
          if (answersText.trim() && Array.isArray(updated.data.answers)) {
            const translatedAnswers = answersText
              .split(/\r?\n/)
              .map(l => l.replace(/^\s*[-*]\s*/, '').trim())
              .filter(Boolean);
            const newAnswers = [...updated.data.answers];
            translatedAnswers.forEach((ans, idx) => {
              if (!newAnswers[idx]) return;
              newAnswers[idx] = { ...newAnswers[idx], enText: ans.trim() };
            });
            updated.data.answers = newAnswers;
          }
          return updated;
        }));
      })
      .catch(err => console.error('Translation error:', err))
      .finally(() => setIsTranslating(false));
  }, [isTranslating, data.text, data.answers, data.flowId, getNodes, getEdges, id, setNodes, API_BASE_URL]);

  return (
    <div 
      className="react-flow__node-default"
      tabIndex={0} // Делаем элемент фокусируемым
      onKeyDown={handleKeyDown}
      style={{ position: 'relative' }}
    >

      <Handle
        type="target"
        position={Position.Left}
        id="input"
        style={{ 
          left: 0,
          top: 24,
          transform: 'translateY(-50%)',
        }}
      />

      {data.audioUrl && (
        <Box sx={{ p: 0, m: 0 }}>
          <AudioPlayer audioUrl={data.audioUrl} onDelete={handleDeleteAudio} />
        </Box>
      )}
      
      <div style={{ position: 'relative' }}>
        <textarea
          ref={textareaRef}
          className="textarea-widget"
          style={{ 
            cursor: isEditable ? 'text' : 'pointer',
            userSelect: isEditable ? 'text' : 'none',
            WebkitUserSelect: isEditable ? 'text' : 'none',
            MozUserSelect: isEditable ? 'text' : 'none',
            msUserSelect: isEditable ? 'text' : 'none'
          }}
          readOnly={!isEditable}
          value={getCurrentText()}
          onClick={selected ? handleTextClick : null}
          onChange={handleTextChange}
          placeholder="Enter text..."
          onBlur={onBlur}
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          autoFocus
          onContextMenu={handleContextMenu}
          onKeyDown={(e) => {
            if (e.key === 'Tab' && suggestion) {
              e.preventDefault();
              setNodes((nds) =>
                nds.map((node) => {
                  if (node.id === id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        text: node.data.text + suggestion
                      }
                    };
                  }
                  return node;
                })
              );
              setSuggestion('');
            }
          }}
        />
        {suggestion && (
          <div style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            padding: '13px 16px 11px 16px',
            fontSize: '14px',
            lineHeight: '1.5',
            color: 'rgba(255, 255, 255, 0.4)',
            pointerEvents: 'none',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            fontFamily: 'monospace',
            letterSpacing: 'normal',
            wordSpacing: 'normal',
            textIndent: '0px',
            textAlign: 'start',
            appearance: 'auto'
          }}>
            <span style={{ opacity: 0 }}>
              {data.text || ''}
            </span>
            <span style={{ opacity: 1, backgroundColor: 'rgba(50, 50, 50, 1)' }}>
              <span style={{ opacity: 0.6 }}>{suggestion}</span>
              <span style={{ 
                backgroundColor: 'rgba(55, 55, 55, 0.85)',
                padding: '0 4px',
                borderRadius: '3px',
                marginLeft: '4px',
                fontSize: '0.9em',
                color: 'white'
              }}>
                ⇥ tab
              </span>
            </span>
          </div>
        )}

        {/* Ответы */}
        {data.answers?.map((answer, index) => (
          <div key={index} style={{ position: 'relative', margin: 0, padding: 0 }}>
            {selected && (
              <IconButton
                size="small"
                onClick={(e) => handleDeleteAnswer(index, e)}
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: '55%',
                  transform: 'translateY(-50%)',
                  color: 'disabled',
                  opacity: 0.7,
                  '&:hover': {
                    opacity: 1,
                    backgroundColor: 'error.main',
                    color: 'white'
                  }
                }}
              >
                <CloseIcon fontSize="small" sx={{ width: 16, height: 16 }} />
              </IconButton>
            )}
            <textarea
              ref={el => answerRefs.current[index] = el}
              className="textarea-widget answer-widget"
              style={{ 
                paddingLeft: 32,
                textAlign: 'right',
                cursor: isEditable ? 'text' : 'pointer',
                userSelect: isEditable ? 'text' : 'none',
                WebkitUserSelect: isEditable ? 'text' : 'none',
                MozUserSelect: isEditable ? 'text' : 'none',
                msUserSelect: isEditable ? 'text' : 'none',
                resize: 'none',
                overflow: 'hidden'
              }}
              readOnly={!isEditable}
              value={getCurrentAnswer(answer)}
              onClick={selected ? handleAnswerClick : null}
              onChange={(e) => handleAnswerChange(index, e.target.value)}
              placeholder="Enter answer..."
              onMouseDown={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
              onBlur={onBlur}
              rows={1}
              onContextMenu={handleContextMenu}
            />
            <Handle
              type="source"
              position={Position.Right}
              id={`answer-${index}`}
              style={{ 
                right: 0,
                top: '50%',
                transform: 'translateY(-45%)'
              }}
            />
          </div>
        ))}

        {/* Показываем кнопку Add Answer только если ответов меньше 5 */}
        {selected && (
          <div style={{ 
            position: 'absolute',
            bottom: -30,
            right: 0,
            zIndex: 1000
          }}>
            <Chip 
              icon={isTranslating ? <CircularProgress size={16} /> : null}
              label={isTranslating ? '' : 'RU > EN'}
              clickable
              color="primary"
              size="small"
              sx={{ marginRight: 0.5 }}
              onClick={handleTranslate}
              disabled={isTranslating || (!((data.text||'').trim() || (data.answers||[]).some(a => (a.text||'').trim())))}
            />
            {(!data.answers || data.answers.length < 5) && (
              <Chip 
                icon={<AddRounded />}
                label="Answer"
                clickable
                color="primary"
                size="small"
                sx={{ marginLeft: 0.5 }}
                onClick={handleAddAnswer}
              />
            )}
          </div>
        )}

        {!hasAnswers && (
          <Handle
            type="source"
            position={Position.Right}
            id="default"
            style={{ 
              right: 0,
              top: '100%',
              transform: 'translateY(-170%)'
            }}
          />
        )}
      </div>
    </div>
  );
};

